// @flow

import type {
  Model,
  Image,
  ProductType,
} from '../../../types/pdb';
import type { State } from '../types';
import type { State as RouterState } from '../../router/types';

import {
  getPdbItem,
} from './main';

import {
  getReservationExpires,
  getPrice as getPackagePrice,
  getDiscountPrice as getPackageDiscountPrice,
  getDefaultImageUrl as getPackageDefaultImageUrl,
  hasDiscount as packageHasDiscount,
  getPackageIdFromUrl,
} from './packages';

type GlobalState = {
  pdb: State,
  router: RouterState,
};

export const getModelData = (state: GlobalState, pdbId: ?string = null): Model => {
  const pdbItem = getPdbItem(state, pdbId);
  return pdbItem.model;
};

export const getModelId = (state: GlobalState, pdbId?: ?string = null): number => {
  const model = getModelData(state, pdbId);
  return model.id;
};

export const getModel = (state: GlobalState, pdbId?: ?string = null): string => {
  const model = getModelData(state, pdbId);
  return model.model;
};

export const getDescription = (state: GlobalState, pdbId?: ?string = null): string => {
  const model = getModelData(state, pdbId);
  return model.description;
};

export const getMake = (state: GlobalState, pdbId?: ?string = null): string => {
  const model = getModelData(state, pdbId);
  return model.make;
};

export const getPrice = (
  state: GlobalState,
  pdbId?: ?string = null,
  packageId?: ?string,
): string => {
  const model = getModelData(state, pdbId);

  const pkgId = packageId || getPackageIdFromUrl(state);
  // If a package is selected, we need to add the package price to the model price.
  if (pkgId) {
    return String(parseInt(model.price, 10) + getPackagePrice(state, Number(pkgId), pdbId));
  }
  return model.price;
};

export const getSize = (state: GlobalState, pdbId?: ?string = null): string => {
  const model = getModelData(state, pdbId);
  return model.size;
};

export const getBodystyle = (state: GlobalState, pdbId?: ?string = null): string => {
  const model = getModelData(state, pdbId);
  return model.bodystyle;
};

export const hasDiscount = (
  state: GlobalState,
  pdbId?: ?string = null,
  packageId?: ?string,
): boolean => {
  const model = getModelData(state, pdbId);
  const pkgId = packageId || getPackageIdFromUrl(state);
  // If either the model or the package has a discount, we are dealing with discount (changes
  // display components in f.e. the carblock).
  let discount = Boolean(model.discount) && parseInt(model.discount, 10) > 0;
  if (!discount && pkgId && packageHasDiscount(state, Number(pkgId), pdbId)) {
    discount = true;
  }
  return discount;
};

export const getDiscountPrice = (
  state: GlobalState,
  pdbId?: ?string = null,
  packageId?: ?string,
): number => {
  const model = getModelData(state, pdbId);
  const modelPrice = hasDiscount(state, pdbId)
    ? parseInt(model.price, 10) - parseInt(model.discount, 10)
    : parseInt(model.price, 10);

  // If a package is selected, we need to add the package discount price to the model price.
  const pkgId = packageId || getPackageIdFromUrl(state);
  if (pkgId) {
    return modelPrice + getPackageDiscountPrice(state, Number(pkgId), pdbId);
  }
  return modelPrice;
};

export const getFueltype = (state: GlobalState, pdbId?: ?string = null): string => {
  const model = getModelData(state, pdbId);
  return model.fueltype;
};

export const getProductType = (state: GlobalState, pdbId?: ?string = null): ProductType => {
  const model = getModelData(state, pdbId);
  return model.product_type;
};

export const getTransmission = (state: GlobalState, pdbId?: ?string = null): string => {
  const model = getModelData(state, pdbId);
  return model.transmission;
};

export const getDoorCount = (state: GlobalState, pdbId?: ?string = null): number => {
  const model = getModelData(state, pdbId);
  return model.nbofdoor;
};

export const getKmPrice = (state: GlobalState, pdbId?: ?string = null): number => {
  const model = getModelData(state, pdbId);
  return model.km_price;
};

export const getKmPriceInEuros = (state: GlobalState, pdbId?: ?string = null): number => {
  const model = getModelData(state, pdbId);
  return model.km_price / 100;
};

export const getDefaultImage = (state: GlobalState, pdbId?: ?string = null): ?Image => {
  const model = getModelData(state, pdbId);
  return model.default_image || null;
};

export const getManufactured = (state: GlobalState, pdbId?: ?string = null): ?string => {
  const model = getModelData(state, pdbId);
  return model.manufactured || null;
};

export const getOdometer = (state: GlobalState, pdbId?: ?string = null): ?string => {
  const model = getModelData(state, pdbId);
  return model.odometer || null;
};

export const getLicense = (state: GlobalState, pdbId?: ?string = null): ?string => {
  const model = getModelData(state, pdbId);
  return model.license || null;
};

export const getDamageDescription = (state: GlobalState, pdbId?: ?string = null): ?string => {
  const model = getModelData(state, pdbId);
  return model.damage_description || null;
};

export const getDefaultImageUrl = (
  state: GlobalState,
  pdbId?: ?string = null,
  packageId?: ?string,
): ?string => {
  // If a package is selected, we return the package image url.
  const pkgId = packageId || getPackageIdFromUrl(state);
  if (pkgId) {
    const packageImageUrl = getPackageDefaultImageUrl(state, pkgId, pdbId);
    if (packageImageUrl) {
      return packageImageUrl;
    }
  }

  // If no package was selected, or the package doesn't have an image, we return the model image.
  const model = getModelData(state, pdbId);
  return model.default_image && model.default_image.url ? model.default_image.url : null;
};

export const getImages = (state: GlobalState, pdbId?: ?string = null): ?Image[] => {
  const model = getModelData(state, pdbId);
  return model.images;
};

export const getReservation = (state: GlobalState, pdbId?: ?string = null): ?boolean => {
  const model = getModelData(state, pdbId);
  const reservationExpiresResponse = getReservationExpires(state, pdbId);
  let response = false;
  if (model.product_type === 'Occasion') {
    const reservationExpiresTime = new Date(reservationExpiresResponse);
    const currentTime = new Date();
    if (reservationExpiresTime > currentTime) {
      response = true;
    }
  }
  return response;
};

export const getDefaultDeliveryTime = (state: GlobalState, pdbId?: ?string = null): ?number => {
  const model = getModelData(state, pdbId);
  return model.default_deliverytime;
};

export const isAvailable = (state: GlobalState, pdbId?: ?string = null): boolean => {
  const { not_available, stock_only, hasStock } = getModelData(state, pdbId);
  return !(not_available || (stock_only && !hasStock));
};

export const getIsNotOrderable = (state: GlobalState, pdbId?: ?string = null): boolean => {
  const model = getModelData(state, pdbId);
  return model.isNotOrderable === true;
};
